// src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authService, tenantService } from '../services/api';
import { useLanguage } from './LanguageContext';
import { hasFeature as resolveFeature, getLimit as resolveLimit } from '../utils/featureGating';
import host from '../host';
import { resetDeviceSession } from '../platform/backendBinding';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const { resetTranslations, t } = useLanguage();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userRole, setUserRole] = useState('');
    const [username, setUsername] = useState('');
    const [employeeId, setEmployeeId] = useState('');      // Add this
    const [authType, setAuthType] = useState('');          // Add this ('tenant', 'employee', 'direct')
    const [isLoading, setIsLoading] = useState(true);
    const [defaultPrinter, setDefaultPrinter] = useState('');
    const [printerConfigured, setPrinterConfigured] = useState(false);
    // New tenant-related state
    const [tenantConfig, setTenantConfig] = useState(null);
    const [tenantId, setTenantId] = useState('');
    const [companyId, setCompanyId] = useState('');
    const [selectedPortal, setSelectedPortalState] = useState(
        () => localStorage.getItem('selectedPortal') || 'pos'
    );
    const [permissions, setPermissions] = useState([]);
    const [enabledFeatures, setEnabledFeatures] = useState(null);
    const [limits, setLimits] = useState({});

    // Helper function to decode JWT token and extract tenant info
    const decodeToken = (token) => {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return {
                username: payload.sub,
                role: payload.role,
                tenantId: payload.tenant_id,
                exp: payload.exp
            };
        } catch (error) {
            console.error('Error decoding token:', error);
            return null;
        }
    };

    // Fetch tenant configuration.
    //
    // `isBootRestore` (F12a-N task-n8 Fix A — marker-independent self-heal):
    // pass true ONLY from the boot session-restore path (checkAuth() below,
    // the one call site that runs while replaying a PERSISTED token rather
    // than one just issued by a fresh login/employeeLogin). On capacitor, a
    // 401 restoring a stored token can only mean the token is foreign to
    // the backend this build now talks to — `platform/backendBinding.js`'s
    // marker comparison is blind to a device that was already poisoned
    // before its marker existed (or whose marker was itself written while
    // already poisoned, see that file's "KNOWN LIMITATION" note) — the
    // backend's own 401 is ground truth regardless of what the marker says.
    // Scoped tightly to isBootRestore + capacitor so an ordinary mid-session
    // 401 (a token that genuinely expires during live use) is NEVER treated
    // this way — that already has its own handling (the axios interceptor's
    // device-revoked/expired events in services/api.js) and must not be
    // widened into an automatic wipe+reload.
    const fetchTenantConfig = async (tenant_id, { isBootRestore = false } = {}) => {
        try {
            const config = await tenantService.getTenantConfig();
            setTenantConfig(config);

            // Store in localStorage for persistence
            localStorage.setItem('tenantConfig', JSON.stringify(config));
            localStorage.setItem('tenantId', tenant_id);

            return config;
        } catch (error) {
            console.error('Error fetching tenant config:', error);

            if (isBootRestore && host.type === 'capacitor' && error?.response?.status === 401) {
                console.log(` ${host.type} boot restore: 401 with a stored token present - foreign/dead session, resetting device`);
                try {
                    await resetDeviceSession();
                } finally {
                    window.location.reload();
                }
                // Reload is in flight (async on some platforms) — return a
                // harmless placeholder in case a caller reads this before it
                // lands; nothing further should meaningfully render.
                return null;
            }

            // Fallback to stored config or default
            const storedConfig = localStorage.getItem('tenantConfig');
            if (storedConfig) {
                const config = JSON.parse(storedConfig);
                setTenantConfig(config);
                return config;
            }

            // Final fallback
            const fallbackConfig = {
                brand_name: "Aqana",
                brand_color: "#3B82F6",
                currency: "£"
            };
            setTenantConfig(fallbackConfig);
            return fallbackConfig;
        }
    };

    // Update the persistAuth function
    const persistAuth = (isAuth, role, name, tenant_id, printerConfig = false) => {
        if (isAuth) {
            localStorage.setItem('isAuthenticated', 'true');
            if (role) localStorage.setItem('user_role', role);
            if (name) localStorage.setItem('username', name);
            if (tenant_id) localStorage.setItem('tenantId', tenant_id);
            localStorage.setItem('printerConfigured', printerConfig ? 'true' : 'false');
        } else {
            localStorage.removeItem('isAuthenticated');
            localStorage.removeItem('user_role');
            localStorage.removeItem('username');
            localStorage.removeItem('tenantId');
            localStorage.removeItem('tenantConfig');
            localStorage.removeItem('printerConfigured');

            localStorage.removeItem('employee_id');
            localStorage.removeItem('employee_name');
            localStorage.removeItem('auth_type');
        }
    };

    useEffect(() => {
        console.log(' AuthContext: Checking authentication...');

        // Skip authentication check for landing pages completely
        const landingPages = ['/login', '/system-access', '/', '/how-it-works', '/our-services', '/about-us', '/faq', '/contact-us'];

        // Use pathname for BrowserRouter (web), fall back to hash for HashRouter (Electron)
        const currentPath = host.usesHashRouter
            ? (window.location.hash ? window.location.hash.replace('#', '') : '/')
            : window.location.pathname;

        console.log(' Current path:', currentPath);
        console.log(' Full location:', window.location.href);

        if (landingPages.includes(currentPath)) {
            // In Electron: persistent session via sync token — skip login if device is set up
            if (host.isPosDevice) {
                const hasSyncToken = localStorage.getItem('sync_token');
                const hasOfflineData = localStorage.getItem('offline_data_downloaded') === 'true';
                const savedTenantId = localStorage.getItem('tenantId');

                if (hasSyncToken && hasOfflineData && savedTenantId) {
                    console.log(' Electron persistent session - restoring from sync token');
                    // Fall through to the token-restore logic below
                } else if (localStorage.getItem('token')) {
                    console.log(` ${host.type} restart with existing token - restoring session`);
                    // Fall through to the token-restore logic below
                } else {
                    console.log(' Landing page - skipping auth check');
                    setIsLoading(false);
                    return;
                }
            } else {
                console.log(' Landing page - skipping auth check');
                setIsLoading(false);
                return;
            }
        }
        setIsLoading(true);

        const checkAuth = async () => {
            const token = localStorage.getItem('token');
            const hasSyncToken = localStorage.getItem('sync_token');
            const hasOfflineData = localStorage.getItem('offline_data_downloaded') === 'true';
            const authenticated = !!token || (!!hasSyncToken && hasOfflineData);

            console.log(' Token exists:', !!token, 'Sync token:', !!hasSyncToken);

            if (authenticated) {
                // Restore user state from localStorage
                const storedUserRole = localStorage.getItem('user_role');
                const storedUsername = localStorage.getItem('username');
                const storedTenantId = localStorage.getItem('tenantId');
                const storedEmployeeId = localStorage.getItem('employeeId');
                const storedAuthType = localStorage.getItem('authType');
                const storedCompanyId = localStorage.getItem('companyId');
                const storedSelectedPortal = localStorage.getItem('selectedPortal') || 'pos';
                setSelectedPortalState(storedSelectedPortal);

                console.log(' Restoring user:', { storedUsername, storedUserRole, storedTenantId });

                // Set the auth state
                setIsAuthenticated(true);
                setUserRole(storedUserRole || '');
                setUsername(storedUsername || '');
                setTenantId(storedTenantId || '');
                setEmployeeId(storedEmployeeId || '');
                setAuthType(storedAuthType || '');
                setCompanyId(storedCompanyId || '');

                const storedPermissions = localStorage.getItem('permissions');
                if (storedPermissions) {
                    try {
                        setPermissions(JSON.parse(storedPermissions));
                    } catch (e) {
                        // ignore parse errors
                    }
                }
                const storedFeatures = localStorage.getItem('enabledFeatures');
                if (storedFeatures) {
                    try { setEnabledFeatures(JSON.parse(storedFeatures)); } catch (e) {}
                }
                const storedLimits = localStorage.getItem('limits');
                if (storedLimits) {
                    try { setLimits(JSON.parse(storedLimits)); } catch (e) {}
                }

                // Fetch tenant configuration if we have a tenant ID
                if (storedTenantId) {
                    console.log(' Fetching tenant config for:', storedTenantId);
                    // isBootRestore: true — this call is replaying a
                    // PERSISTED token, not a fresh login (Fix A, see
                    // fetchTenantConfig's header comment).
                    await fetchTenantConfig(storedTenantId, { isBootRestore: true });

                    // Fix A's self-heal may have fired inside the call above
                    // (foreign/dead session — storage cleared + reload
                    // requested). Stop here rather than falling through to
                    // the redirect/setIsLoading below with now-stale local
                    // variables; the reload restarts boot cleanly. Detected
                    // via the token's absence — resetDeviceSession() is what
                    // removes it, so this is a reliable, implicit signal
                    // without a second parallel return-value channel.
                    if (!localStorage.getItem('token')) {
                        return;
                    }
                }

                // In Electron: always redirect to FunctionsPage (POS-only)
                if (host.isPosDevice) {
                    console.log(' Session restored - redirecting to POS functions');
                    window.location.hash = '#/functions';
                }

            } else {
                console.log(' No token found');
                if (currentPath !== '/login' && currentPath !== '/') {
                    console.log(' Redirecting to login from:', currentPath);
                    window.location.hash = '#/login';
                }
            }
            setIsLoading(false);
            console.log(' Auth check complete');
        };

        checkAuth();

        // Don't add storage listener to prevent loops
        return () => {
            console.log(' AuthContext cleanup');
        };
    }, []); // Empty dependency array - only run once on mount

    const setPrinterAsConfigured = () => {
        setPrinterConfigured(true);
        localStorage.setItem('printerConfigured', 'true');
    };

    const storeGatingPayload = async (data) => {
        if (data.enabled_features) {
            setEnabledFeatures(data.enabled_features);
            localStorage.setItem('enabledFeatures', JSON.stringify(data.enabled_features));
        }
        if (data.limits) {
            setLimits(data.limits);
            localStorage.setItem('limits', JSON.stringify(data.limits));
        }
    };

    const setAuthenticatedState = (authData) => {
        setIsAuthenticated(authData.isAuthenticated);
        setUserRole(authData.userRole || '');
        setUsername(authData.username || '');
        setTenantId(authData.tenantId || '');
        setEmployeeId(authData.employeeId || '');
        setAuthType(authData.authType || '');
        setCompanyId(authData.companyId || '');

        // Persist to localStorage
        if (authData.isAuthenticated) {
            localStorage.setItem('isAuthenticated', 'true');
            localStorage.setItem('user_role', authData.userRole || '');
            localStorage.setItem('username', authData.username || '');
            localStorage.setItem('tenantId', authData.tenantId || '');
            localStorage.setItem('employeeId', authData.employeeId || '');
            localStorage.setItem('authType', authData.authType || '');
            localStorage.setItem('companyId', authData.companyId || '');
            if (authData.selectedPortal) {
                setSelectedPortalState(authData.selectedPortal);
                localStorage.setItem('selectedPortal', authData.selectedPortal);
            }
        }
    };

    const login = async (username, password, shortId = null, printer = '') => {
        try {
            setIsLoading(true);

            console.log(' Authenticating against remote server');
            // Use tenant login by default for backward compatibility
            const data = await authService.tenantLogin(username, password, shortId);

            // Store sync token for persistent device-level sync auth
            if (data.sync_token) {
                localStorage.setItem('sync_token', data.sync_token);
            }
            if (data.sync_token_expires_at) {
                localStorage.setItem('token_expires_at', data.sync_token_expires_at);
            }

            const authStateData = {
                isAuthenticated: true,
                userRole: data.user_role || '',
                username: username,
                tenantId: data.tenant_id || '',
                companyId: data.company_id || '',
                authType: 'tenant'
            };


            // In Electron, always force POS mode
            if (host.isPosDevice) {
                authStateData.selectedPortal = 'pos';
            }

            setAuthenticatedState(authStateData);

            if (data.permissions || data?.permissions) {
                const perms = data.permissions;
                setPermissions(perms);
                localStorage.setItem('permissions', JSON.stringify(perms));
            }
            await storeGatingPayload(data);

            // Store tenant_id with underscore
            if (data.tenant_id) {
                localStorage.setItem('tenant_id', data.tenant_id);
            }

            if (printer) {
                setDefaultPrinter(printer);
                localStorage.setItem('defaultPrinter', printer);
            }

            // Fetch tenant configuration
            if (data.tenant_id) {
                await fetchTenantConfig(data.tenant_id);
            }

            setIsLoading(false);
            return data;
        } catch (error) {
            console.error(' AuthContext: Login error:', error);
            setIsLoading(false);
            throw error;
        }
    };

    const employeeLogin = async (tenantId, pin, printer = '') => {
        try {
            setIsLoading(true);
            const data = await authService.employeeLogin(tenantId, pin);
            localStorage.setItem('lastUsername', localStorage.getItem('username') || 'tenant-admin');
            localStorage.setItem('lastUserRole', localStorage.getItem('user_role') || 'admin');
            setAuthenticatedState({
                isAuthenticated: true,
                userRole: data.user_role || '',
                username: data.employee_name || '',
                tenantId: tenantId,
                employeeId: data.employee_id || '',
                authType: 'employee'
            });

            await storeGatingPayload(data);

            if (printer) {
                setDefaultPrinter(printer);
                localStorage.setItem('defaultPrinter', printer);
            }

            await fetchTenantConfig(tenantId);

            setIsLoading(false);
            return data;
        } catch (error) {
            setIsLoading(false);
            throw error;
        }
    };

    const superAdminLogin = async (username, password) => {
        try {
            setIsLoading(true);
            const data = await authService.superAdminLogin(username, password);

            setAuthenticatedState({
                isAuthenticated: true,
                userRole: 'super_admin',
                username: data.username || username,
                authType: 'super_admin'
            });

            setIsLoading(false);
            return data;
        } catch (error) {
            setIsLoading(false);
            throw error;
        }
    };

    const isSuperAdmin = userRole === 'super_admin';

    const logout = useCallback(async () => {
        // Capacitor stations are host.isPosDevice with no local DB — a
        // logout there is an OPERATOR logout (shift/EOD handover), not a
        // device reset. PosAppShell keeps rendering this station regardless
        // of auth state, so the device must stay bound to its tenant.
        const isCapacitorStation = host.isPosDevice;

        if (isCapacitorStation) {
            // Clear only the operator's session; tenant identity, tenant
            // config, and feature gating (device-level facts set once at
            // enrollment) are left untouched below.
            authService.logout();
            localStorage.removeItem('permissions');
            localStorage.removeItem('credentialType');
            localStorage.removeItem('auth_type');
            localStorage.removeItem('cached_translations');
            localStorage.removeItem('token_expires_at');
            localStorage.removeItem('employee_id');
            localStorage.removeItem('employee_name');
            localStorage.removeItem('employeeId');
        } else {
            // Web mode: just clear auth state
            authService.logout();
            localStorage.removeItem('permissions');
            localStorage.removeItem('credentialType');
            localStorage.removeItem('auth_type');
            localStorage.removeItem('cached_translations');
            localStorage.removeItem('token_expires_at');
            persistAuth(false, '', '', '', false);
        }

        // Gating flags are tenant-level, not operator-level — a capacitor
        // station keeps them (see above).
        if (!isCapacitorStation) {
            localStorage.removeItem('enabledFeatures');
            localStorage.removeItem('limits');
        }

        // Reset operator/session React state (always — every runtime signs
        // the current operator/user out).
        setIsAuthenticated(false);
        setUserRole('');
        setUsername('');
        setEmployeeId('');
        setAuthType('');
        setCompanyId('');
        setPrinterConfigured(false);
        setPermissions([]);
        setSelectedPortalState('pos');
        resetTranslations();

        // Tenant identity + gating only drop in web mode — a capacitor
        // station keeps them so it can render its own operator lock screen
        // instead of bricking.
        if (!isCapacitorStation) {
            setTenantId('');
            setTenantConfig(null);
            setEnabledFeatures(null);
            setLimits({});
        }

        // Navigate to login
        if (host.usesHashRouter) {
            window.location.hash = '#/login';
        } else {
            window.location.href = '/login';
        }
    }, [resetTranslations]);

    // Listen for device revocation / expiry events from the API interceptor
    useEffect(() => {
        const handleDeviceRevoked = () => {
            alert(t('device_reset_by_admin'));
            logout();
        };
        const handleDeviceExpired = () => {
            alert(t('device_session_expired'));
            logout();
        };

        // The USER's JWT lapsed (24h lifetime). Before this existed, every
        // caller rendered its own failure copy for a dead session — the
        // Manager Portal told a pilot tenant to "check your connection" for a
        // whole day when the only fix was to sign in again. Ending the
        // session is the one action that resolves it.
        //
        // Guarded so a burst of parallel 401s (the portal fires several
        // requests per screen) cannot stack alerts or call logout repeatedly.
        let sessionExpiryHandled = false;
        const handleSessionExpired = () => {
            if (sessionExpiryHandled) return;
            sessionExpiryHandled = true;
            alert(t('session_expired_sign_in_again', {}, 'Your session has expired. Please sign in again.'));
            logout();
        };

        window.addEventListener('device-revoked', handleDeviceRevoked);
        window.addEventListener('device-expired', handleDeviceExpired);
        window.addEventListener('session-expired', handleSessionExpired);

        return () => {
            window.removeEventListener('device-revoked', handleDeviceRevoked);
            window.removeEventListener('device-expired', handleDeviceExpired);
            window.removeEventListener('session-expired', handleSessionExpired);
        };
    }, [logout, t]);

    // Helper functions for tenant configuration
    const getTenantSetting = (key, defaultValue = null) => {
        if (!tenantConfig) return defaultValue;

        // Support nested keys like 'theme.primary_color'
        const keys = key.split('.');
        let value = tenantConfig;

        for (const k of keys) {
            value = value?.[k];
            if (value === undefined) return defaultValue;
        }

        return value || defaultValue;
    };

    const refreshTenantConfig = async () => {
        try {
            const data = await tenantService.getTenantConfig();
            setTenantConfig(data);
            // Update any localStorage cache if you have it
            localStorage.setItem('tenantConfig', JSON.stringify(data));
        } catch (error) {
            console.error('Failed to refresh tenant config:', error);
        }
    };

    const hasPermission = (key) => {
        // If no permissions loaded (legacy user), allow all
        if (!permissions || permissions.length === 0) return true;
        return permissions.some(p => p.key === key && p.can_view);
    };

    const canEdit = (key) => {
        if (!permissions || permissions.length === 0) return true;
        return permissions.some(p => p.key === key && p.can_edit);
    };

    const hasFeature = (flag) => resolveFeature(enabledFeatures, flag);
    const getLimit = (key) => resolveLimit(limits, key);

    const getBrandName = () => getTenantSetting('brand_name', 'Café POS');
    const getBrandColor = () => getTenantSetting('brand_color', '#3B82F6');
    const getCurrency = (overrideCurrency = null) => {
        if (overrideCurrency) return overrideCurrency;
        if (companyId && !tenantId) {
            // For company-level users, currency will be passed from ReportingContext
            return '£'; // Temporary fallback - this will be overridden
        }
        return getTenantSetting('currency', '£');
    };

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                userRole,
                username,
                tenantId,
                companyId,
                employeeId,
                authType,
                selectedPortal,
                setSelectedPortal: (portal) => {
                    setSelectedPortalState(portal);
                    localStorage.setItem('selectedPortal', portal);
                },
                tenantConfig,
                printerConfigured,
                setPrinterAsConfigured,
                isLoading,
                login,
                employeeLogin,
                superAdminLogin,
                isSuperAdmin,
                logout,
                setAuthenticatedState,
                // Tenant helper functions
                getTenantSetting,
                refreshTenantConfig,
                getBrandName,
                getBrandColor,
                getCurrency,
                // Permissions
                permissions,
                hasPermission,
                canEdit,
                // Feature gating
                enabledFeatures,
                limits,
                hasFeature,
                getLimit,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;